import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import usePageTitle from '../hooks/usePageTitle';
import api from '../services/api';
import alert from '../utils/alertService';
import './EventDetails.css';

const EventDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isFounder } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEvent();
  }, [id]);

  useEffect(() => {
    if (event) {
      document.title = `${event.title} | ALIENS`;
    }
  }, [event]);

  const fetchEvent = async () => {
    try {
      const response = await api.get(`/events/${id}`);
      setEvent(response.data);
    } catch (error) {
      console.error('Error fetching event:', error);
      alert.error('Event not found');
      navigate('/events');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (status) => {
    try {
      await api.post(`/events/${id}/register`, { status });
      fetchEvent();
    } catch (error) {
      alert.error(error.response?.data?.message || 'Error registering for event');
    }
  };

  const isUserRegistered = () => {
    if (!user || !event?.registrations) return false;
    return event.registrations.some(reg => reg.user?.id === user.id);
  };

  const getUserRegistration = () => {
    if (!user || !event?.registrations) return null;
    return event.registrations.find(reg => reg.user?.id === user.id);
  };

  const getRegistrationCounts = () => {
    if (!event?.registrations) return { coming: 0, notComing: 0 };
    const coming = event.registrations.filter(reg => reg.status === 'coming').length;
    const notComing = event.registrations.filter(reg => reg.status === 'not_coming').length;
    return { coming, notComing };
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
        <div className="loading-text">Loading...</div>
      </div>
    );
  }

  if (!event) {
    return null;
  }

  const defaultImages = [
    'https://images.unsplash.com/photo-1446776653964-20c1d3a81b06?w=800&h=400&fit=crop&q=80&auto=format',
    'https://images.unsplash.com/photo-1462331940025-496dfbfc7564?w=800&h=400&fit=crop&q=80&auto=format',
    'https://images.unsplash.com/photo-1502134249126-9f3755a50d78?w=800&h=400&fit=crop&q=80&auto=format',
  ];
  const defaultImageIndex = (event.id - 1) % defaultImages.length;
  const defaultImage = defaultImages[defaultImageIndex];
  const posterUrl = event.poster_url || (event.poster 
    ? `https://aliensteam.com/storage/${event.poster}?t=${Date.now()}` 
    : defaultImage);

  const counts = getRegistrationCounts();

  return (
    <div className="event-details-container">
      <button className="back-button" onClick={() => navigate('/events')}>
        ← Back to Events
      </button>

      <div className="event-details-header">
        <div className="event-poster-wrapper">
          <img
            key={`event-details-poster-${event.id}-${event.updated_at || Date.now()}`}
            src={posterUrl}
            alt={event.title}
            className="event-poster"
            onError={(e) => {
              if (e.target.src !== defaultImage) {
                e.target.src = defaultImage;
              }
            }}
          />
        </div>
        <h1>{event.title}</h1>
      </div>

      <div className="event-details-content">
        {event.description && (
          <div className="details-section">
            <h2>
              <span className="section-icon">📄</span>
              Description
            </h2>
            <p className="description-text">{event.description}</p>
          </div>
        )}

        <div className="details-section">
          <h2>
            <span className="section-icon">📅</span>
            Event Details
          </h2>
          <div className="details-grid">
            <div className="detail-item">
              <span className="detail-icon">📅</span>
              <div className="detail-content">
                <span className="detail-label">Date</span>
                <span className="detail-value">
                  {new Date(event.event_date).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
            </div>
            
            <div className="detail-item">
              <span className="detail-icon">🕐</span>
              <div className="detail-content">
                <span className="detail-label">Time</span>
                <span className="detail-value">{event.event_time || 'Not specified'}</span>
              </div>
            </div>
            
            {event.creator && (
              <div className="detail-item">
                <span className="detail-icon">👤</span>
                <div className="detail-content">
                  <span className="detail-label">Created By</span>
                  <span className="detail-value">{event.creator.name || 'Unknown'}</span>
                </div>
              </div>
            )}

            {isFounder && event.registrations && (
              <>
                <div className="detail-item coming-count">
                  <span className="detail-icon">✅</span>
                  <div className="detail-content">
                    <span className="detail-label">Coming</span>
                    <span className="detail-value coming-value">
                      {counts.coming} {counts.coming === 1 ? 'person' : 'people'}
                    </span>
                  </div>
                </div>
                <div className="detail-item not-coming-count">
                  <span className="detail-icon">❌</span>
                  <div className="detail-content">
                    <span className="detail-label">Not Coming</span>
                    <span className="detail-value not-coming-value">
                      {counts.notComing} {counts.notComing === 1 ? 'person' : 'people'}
                    </span>
                  </div>
                </div>
                <div className="detail-item">
                  <span className="detail-icon">👥</span>
                  <div className="detail-content">
                    <span className="detail-label">Total Registrations</span>
                    <span className="detail-value">
                      {event.registrations.length} {event.registrations.length === 1 ? 'person' : 'people'}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {isFounder && event.registrations && event.registrations.length > 0 && (
          <div className="details-section">
            <h2>
              <span className="section-icon">👥</span>
              Registered Users ({event.registrations.length})
            </h2>
            <div className="registrations-list">
              {event.registrations.map((reg, index) => (
                <div key={reg.id || index} className="registration-item">
                  <div className="registration-user-info">
                    <span className="user-avatar">
                      {reg.user?.name ? reg.user.name.charAt(0).toUpperCase() : '?'}
                    </span>
                    <span className="user-name">{reg.user?.name || 'Unknown User'}</span>
                  </div>
                  <span className={`status-badge ${reg.status === 'coming' ? 'coming' : 'not-coming'}`}>
                    {reg.status === 'coming' ? '✅ Coming' : '❌ Not Coming'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {isFounder && (!event.registrations || event.registrations.length === 0) && (
          <div className="details-section">
            <div className="empty-state">
              <span className="empty-icon">👥</span>
              <p>No registrations yet. Be the first to register!</p>
            </div>
          </div>
        )}
      </div>

      <div className="event-details-footer">
        {user && !isFounder && !isUserRegistered() && (
          <div className="action-buttons">
            <button
              className="btn-success"
              onClick={() => handleRegister('coming')}
            >
              ✅ I'm Coming
            </button>
            <button
              className="btn-secondary"
              onClick={() => handleRegister('not_coming')}
            >
              ❌ Not Coming
            </button>
          </div>
        )}
        {user && !isFounder && isUserRegistered() && (
          <div className="registration-status-card">
            <div className="status-card">
              <span className="status-icon">
                {getUserRegistration()?.status === 'coming' ? '✅' : '❌'}
              </span>
              <div className="status-info">
                <h4>You are registered</h4>
                <p>Status: <strong>{getUserRegistration()?.status === 'coming' ? 'Coming' : 'Not Coming'}</strong></p>
              </div>
            </div>
          </div>
        )}
        {!user && (
          <div className="login-prompt">
            <p>Please log in to register for this event</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventDetails;

